import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, CardContent, Chip } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import { grey, green } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { withTranslation } from "react-i18next";
import { isNull, isUndefined, size, isArray, isEmpty } from "lodash";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import { DPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import { ResetButton } from "@plannel/components/ActionIconButton";
import SplitterContainer from "@plannel/components/SplitterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import FilterContainer from "@plannel/components/layout/FilterContainer";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import dateUtils from "@plannel/utils/date-util";
import NumberUtil from "@plannel/utils/number-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import ElasticSearchUtil from "@plannel/pages/utils/elastic-search-util";
import codeUtil from "@plannel/pages/utils/code-util";

import useAutoAnalysis from "@plannel/hooks/useAutoAnalysis";
import planConfigService from "@plannel/services/data/plan-config-service";
import bfReviewService from "@plannel/services/dp/bf-review-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import { FS } from "@plannel/utils/font-util";

const algorithms = Object.values(codeUtil.bfAlgorithm);
const bfReviewColor = { background: green[100]};
const { snackBarMessageColor } = ColorUtil;
const { formatDateTime } = dateUtils;

const METRICS_GRID = "METRICS_GRID";
const ALGORITHM_GRID = "ALGORITHM_GRID";

// Forecast Metrics 결과 히트맵 — 지표(행) 안에서 오차가 낮을수록 초록, 높을수록 빨강.
// 지표마다 단위가 달라(%, 절대값) 색은 행별 상대값이다. 최적(최저)은 굵게 + 초록 테두리.
// hsla 반투명이라 라이트/다크 배경 모두에서 자연스럽다.
const fmtMetric = v =>
  (v === null || v === undefined || v === "" || isNaN(Number(v)))
    ? "-"
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
const heatBg = ratio => `hsla(${Math.round(130 - ratio * 122)}, 55%, 48%, 0.20)`;

const MetricsHeatmap = ({ rows, columns }) => (
  <Box component="table" sx={{ borderCollapse: "separate", borderSpacing: "0 4px", width: "100%", fontSize: FS.TITLE }}>
    <Box component="thead">
      <Box component="tr">
        {/* width 1% + nowrap: 지표 라벨 열을 내용 폭으로 접어 첫 셀이 라벨 바로 뒤에 붙게 한다. */}
        <Box component="th" sx={{ width: "1%" }} />
        {columns.map(c => (
          <Box component="th" key={c} sx={{ px: 1.25, py: 0.5, textAlign: "right", whiteSpace: "nowrap", fontWeight: 500, color: "text.secondary" }}>{c}</Box>
        ))}
      </Box>
    </Box>
    <Box component="tbody">
      {rows.map(row => {
        const nums = columns.map(c => Number(row[c])).filter(v => !isNaN(v));
        const min = nums.length ? Math.min(...nums) : 0;
        const max = nums.length ? Math.max(...nums) : 0;
        return (
          <Box component="tr" key={row.metric}>
            {/* 지표 라벨은 알고리즘 헤더(th)와 같은 톤 — 13px/500/자간·대문자 없음/text.secondary. 정렬만 좌측. */}
            <Box component="td" sx={{ width: "1%", pr: 1.5, whiteSpace: "nowrap", verticalAlign: "middle", fontWeight: 500, color: "text.secondary" }}>{row.metric}</Box>
            {columns.map(c => {
              const raw = row[c];
              const v = Number(raw);
              const has = raw !== null && raw !== undefined && raw !== "" && !isNaN(v);
              const ratio = max === min ? 0 : (v - min) / (max - min);
              const isBest = has && max !== min && v === min;
              return (
                <Box component="td" key={c} sx={{ p: 0.4 }}>
                  <Box sx={{
                    px: 1.25, py: 0.75, borderRadius: 1, textAlign: "right", fontVariantNumeric: "tabular-nums",
                    bgcolor: has ? heatBg(ratio) : "transparent",
                    fontWeight: isBest ? 700 : 400,
                    boxShadow: isBest ? "inset 0 0 0 1.5px hsla(130,45%,38%,0.55)" : "none",
                  }}>
                    {fmtMetric(raw)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  </Box>
);
const BfReview = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [metrics, setMetrics] = useState("MAPE");
  const [columnDefs, setColumnDefs] = useState([]);
  const [metricColumnDefs, setMetricColumnDefs] = useState([]);
  const [chartSeriesSpec, setChartSeriesSpec] = useState(null); // { chartType, isYoy, algorithmNames } — 번역 전
  const [algorithmForecastChart, setAlgorithmForecastChart] = useState([]);
  const [algorithmForecast, setAlgorithmForecast] = useState([]);
  const [metricDatas, setMetricDatas] = useState([]);
  const [metricBucket, setMetricBucket] = useState(null); // { period, horizon }
  const algorithmListRef = useRef([]);
  const [defaultAlgirothmList, setDefaultAlgorithmList] = useState([]);
  const [targetList, setTargetList] = useState([]);
  const [createTs, setCreateTs] = useState();
  const [bfParameters, setBfParameters] = useState({});
  const [isPos, setIsPos] = useState(false);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ? reduxViewState.filter : {});
  const [defaultValue, setDefaultValue] = useState(null);
  const [defaultCustomerHrchy, setDefaultCustomerHrchy] = useState(null);
  const [treeload, setTreeload] = useState(false);

  const [metricsGridLoading, setMetricsGridLoading] = useState(false);
  const [algorithmGridLoading, setAlgorithmGridLoading] = useState(false);
  const metricsGridRef = useRef(null);
  const algorithmGridRef = useRef(null);
  const bestAlgorithm = useRef("Prophet");
  const customerSearch = useRef(reduxViewState?.customerFilter ?? "");
  const itemSearch = useRef(reduxViewState?.itemFilter ?? "");

  // ── AI 자동 분석 (useAutoAnalysis 훅 사용) ────────────────────────────────
  const { handleAutoAnalysis, ChatWidgetComponent } = useAutoAnalysis({
    pageId: 'reviewBaseline',
    autoAnalysisPrompt: '[자동분석]',
    buildContextConfig: {
      filters: () => ({
        custHrchy:      filterValue.customerHrchy?.hrchyCd ?? null,
        itemHrchy:      filterValue.category?.hrchyCd ?? null,
        customerFilter: customerSearch.current || null,
        itemFilter:     itemSearch.current || null,
        selectedMetric: metrics,
        algorithms:     algorithmListRef.current,
        bestAlgorithm:  bestAlgorithm.current,
      }),
      gridData: () => metricDatas,
      chartData: () => {
        // chartData: yyyymmdd + 모델별 예측값 + 실적(SA_QTY)만 포함
        const saQtyKey = isPos ? codeUtil.dpMeasure?.POS_SA_QTY.name : codeUtil.dpMeasure.SA_QTY.name;
        const modelNames = algorithmListRef.current.map(cd =>
          algorithms.find(a => a.cd === cd)?.name
        ).filter(Boolean);
        const allowedKeys = new Set(["yyyymmdd", saQtyKey, ...modelNames]);

        return algorithmForecastChart.map(row => {
          const out = {};
          for (const k of Object.keys(row)) {
            if (allowedKeys.has(k) && row[k] != null) {
              out[k === saQtyKey ? "실적" : k] = row[k];
            }
          }
          out.period = row.yyyymmdd;
          return out;
        });
      },
    },
    dependencies: [filterValue, metrics, metricDatas, algorithmForecastChart],
    enabled: true,
  });
  console.log('ChatWidgetComponent : ', ChatWidgetComponent);
  console.log('handleAutoAnalysis: ', handleAutoAnalysis);

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxState.initialize = true;
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const defaultAlgorithmGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ viewName, title, gridId: ALGORITHM_GRID });
    defaultSettings.defaultColDef = {
      flex: 1,
      minWidth: 100,
      resizable: true,
      singleClickEdit: true,
    }
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBtShowLoading = useCallback(() => {
    algorithmGridRef?.current?.api?.setGridOption('loading', true);
    metricsGridRef?.current?.api?.setGridOption('loading', true);
  }, [])

  const onBtHide = useCallback(() => {
    algorithmGridRef?.current?.api?.setGridOption('loading', false);
    metricsGridRef?.current?.api?.setGridOption('loading', false);
  }, [])

  const setMetricGridHeader = () => {
    const colDefs = [
      { headerName: "metrics", field: "metric", width: 300 },
    ];
    for (const index of algorithmListRef?.current) {
      const headerName = algorithms.find(x => x.cd === index).name;
      colDefs.push(
        {
          headerName, field: headerName, width: 100, singleClickEdit: false, type: ['rightAligned'],
          valueFormatter: (data) => {
            if (isNull(data.value) || isUndefined(data.value)) {
              return "";
            }
            return NumberUtil.formatNumberData(data, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
          }
        });
    }
    setMetricColumnDefs(colDefs);
  }

  /* 번역 전 구성만 담는다. t() 는 아래 useMemo(렌더 시점)에서 평가한다. */
  const setChartSeriesData = (parameter, chartType, isYoy, isPos) => {
    const algorithmNames = []
    if (!isYoy && Object.keys(parameter.algorithm).length > 0) {
      for (const algo of Object.keys(parameter.algorithm)) {
        if (!algorithmListRef?.current.includes(algo)) {
          continue;
        }
        algorithmNames.push(algorithms.find(value => value.cd === algo).name);
      }
    }

    setChartSeriesSpec({ chartType, isYoy, algorithmNames, isPos });
    setTargetList(algorithmNames)
  }

  /*
   * 시리즈 텍스트를 조회 콜백에서 t() 로 만들어 state 에 넣으면, 언어를 바꿔도 옛 언어가 그대로
   * 남는다(차트는 페이지가 넘긴 문자열을 그릴 뿐이다). 렌더 시점에 t 와 함께 다시 만든다.
   */
  const chartSeries = useMemo(() => {
    if (isEmpty(chartSeriesSpec)) {
      return [];
    }

    const { chartType, isYoy, algorithmNames } = chartSeriesSpec;
    const chart = [];
    if (isYoy) {
      chart.push({
        field: codeUtil.dpMeasure.YOY_QTY.name, text: t(codeUtil.dpMeasure.YOY_QTY.name), type: chartType, typeOptions: { strokeWidth: 3 }, orientation: "left", groupName: t("forecast"), backgroundColor: "#eee"
      });
    } else {
      // 알고리즘 이름은 서버/코드 정의값이라 번역 대상이 아니다.
      for (const algorithmName of algorithmNames) {
        chart.push({
          field: algorithmName, text: algorithmName, type: chartType, typeOptions: { strokeWidth: 3 }, orientation: "left", groupName: t("forecast"), backgroundColor: "#eee"
        });
      }
    }
    if (isPos) {
      chart.push({ field: codeUtil.dpMeasure.POS_SA_QTY.name, text: t(codeUtil.dpMeasure.POS_SA_QTY.name), type: "area", typeOptions: { fill: grey[400], stroke: grey[400] }, orientation: "left", groupName: t("forecast"), })
    } else {
      chart.push({ field: codeUtil.dpMeasure.SA_QTY.name, text: t(codeUtil.dpMeasure.SA_QTY.name), type: "area", typeOptions: { fill: grey[400], stroke: grey[400] }, orientation: "left", groupName: t("forecast"), })
    }

    return chart;
  }, [chartSeriesSpec, t, isPos]);

  const clearBfReview = () => {
    setAlgorithmForecastChart([])
    setAlgorithmForecast([])
    setMetricDatas([]);
  }
  const retrieveParameters = () => {
    const param = {
      moduleCd: "DP",
      configCd: codeUtil.dpConfig.BF_PARAMETER,
      activeFlg: true
    };
    planConfigService.getConfigCd(param)
      .then(response => {
        if (response?.data || response?.data !== "") {
          const objVal = response.data.objVal;
          algorithmListRef.current = objVal ? Object.keys(objVal.algorithm) : [objVal?.default];
          setDefaultAlgorithmList(objVal ? Object.keys(objVal.algorithm) : [objVal?.default])
          setMetrics(objVal.bestSelection.metrics)
          setBfParameters(objVal)
        }
        else {
          algorithmListRef.current = ['PR'];
          const params = {
            algorithm: {'PR': {}}
          }
          setBfParameters(params);
        }
        setReload(true);
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      });
  }

  const setForecastGrid = (result) => {
    const headers = result.headersString;
    const colDefs = [
      { headerName: "forecastMethod", field: "forecastName", width: 300 },
    ];
    for (const index of headers) {
      colDefs.push(
        {
          headerName: index, field: index, width: 100, singleClickEdit: false, type: ["numberColumn", "rightAligned"], chartDataType: 'series',
          colId: GridUtils.IGNORE_P13N_COLLID + '_' + index,
          cellStyle: params => {
            if (params.data.forecastName === bestAlgorithm.current) {
              return bfReviewColor;
            }
            return null;
          },
        });
    }
    setColumnDefs(colDefs);

    if (result?.data) {
      const chartData = [];
      const gridData = [];
      const headers = result.chartHeaders ?? [];
      const bUnit = result?.planVersion?.planConfigObj?.BUCKET_UNIT ?? "";

      const getFormatDate = (index) => {
        if (codeUtil.isWeekBucket(bUnit)) {
          const weekHeaders = result.weekHeader;
          return weekHeaders[headers[index]];
        } else {
          return dateUtils.headerDateFormatter(new ZDate(headers[index]), bUnit);
        }
      }
      const names = Object.keys(result.data);
      for (let idx = 0; idx < size(headers); idx++) {
        const cData = {};
        for (let nameIdx = 0; nameIdx < size(result.data); nameIdx++) {
          cData[names[nameIdx]] = result.data[names[nameIdx]][headers[idx]];
        }
        cData.yyyymmdd = getFormatDate(idx);
        chartData.push(cData);
      }

      for (const measureNm of names) {
        if (measureNm !== codeUtil.dpMeasure.SA_QTY.name && measureNm !== codeUtil.dpMeasure.POS_SA_QTY.name) {
          const values = result.data[measureNm];
          const keys = Object.keys(values).sort();
          for (let valIdx = 0; valIdx < size(values); valIdx++) {
            values[getFormatDate(valIdx)] = values[keys[valIdx]];
            delete values[keys[valIdx]];
          }
          if (measureNm === codeUtil.dpMeasure.BF_QTY.name) {
            values.forecastName = "";
          } else if (measureNm === codeUtil.dpMeasure.YOY_QTY.name) {
            values.forecastName = t(codeUtil.dpMeasure.YOY_QTY.name);
          } else {
            values.forecastName = measureNm;
          }
          gridData.push(values);
        }
      }
      setAlgorithmForecastChart(chartData)
      setAlgorithmForecast(gridData)
    } else {
      clearBfReview();
      if (result?.message) {
        const { message } = result;
        setSnackInfo({ open: true, severity: message.type, content: `[${t("forecastAlgorithmResult")}] ${t(message.message)}` });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: `[${t("forecastAlgorithmResult")}] ${t("MSG_NoData")}` });
      }
    }
  }

  const retrieveAlgorithmData = (custHrchies, itemHrchies) => {
    const params = {
      custHrchyFilter: customerSearch.current ? null : custHrchies,
      itemHrchyFilter: itemSearch.current ? null : itemHrchies,
      customerFilter: customerSearch.current,
      itemFilter: itemSearch.current,
      algorithm: [...algorithmListRef?.current, 'YOY']
    }
    if (size(algorithmListRef?.current) > 0) {
      bfReviewService.getGroupbyAlgorithm(params)
        .then(response => {
          if (response.data?.message) {
            return;
          }
          const isYoy = !isEmpty(response.data?.data?.salesActualYoYQty);
          const isPos  = response.data?.planVersion?.posActualFlg;
          setIsPos(isPos);
          if (!isEmpty(bfParameters)) {
            setChartSeriesData(bfParameters, codeUtil.toggleType.BAR, isYoy, isPos);
          }
          setCreateTs([
            { label: t("startTs"), value: formatDateTime(response.data?.status?.startDate) },
            { label: t("endTs"), value: formatDateTime(response.data?.status?.endDate) },
          ]);
          setForecastGrid(response.data);
        })
        .catch(e => {
          debug(e);
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        });
        onBtHide();
    }
  }

  const makeMetricResultGridData = (result) => {
    const pivotData = [];
    const groupByMetric = Object.groupBy(result, ({ metricCd }) => metricCd);
    for (const metricCd of codeUtil.bfMetric) {
      const dataList = groupByMetric[metricCd];
      if (dataList) {
        const data = {"metric" : metricCd};
        for (const row of dataList) {
          const keyName = row.modelName;
          data[keyName] = row.fcstValue;
          if (row.rank && row.rank === 1) {
            bestAlgorithm.current = row.modelName
          }
        }
        pivotData.push(data);
      }
    }
    return pivotData;
  }

  const retrieveMetrics = (custHrchies, itemHrchies) => {
    onBtShowLoading();
    const params = {
      custHrchyFilter: customerSearch.current ? null : custHrchies,
      itemHrchyFilter: itemSearch.current ? null : itemHrchies,
      customerFilter: customerSearch.current,
      itemFilter: itemSearch.current,
      algorithm: defaultAlgirothmList
    };
    if (!params.custHrchyFilter && !params.customerFilter) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoCustomer") });
      clearBfReview();
      onBtHide();
      return;
    }
    if (!params.itemHrchyFilter && !params.itemFilter) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoItem") });
      clearBfReview();
      onBtHide();
      return;
    }
    bfReviewService.getMetrics(params)
    .then(response => {
      if (response.data.message) {
        const { message } = response.data;
        setSnackInfo({ open: true, severity: message.type, content: `[${t("forecastMetricsResult")}] ${t(message.message)}`});
        setMetricDatas([]);
      } else {
        if (response?.data?.data) {
          setMetricDatas(makeMetricResultGridData(response.data.data))
          const dateRange = response.data.dateRange;
          const historyHorizon = response.data.historyHorizon;
          // 칩 2개(기간 · History buckets)로 나눠 보여주려고 문자열 합치기 대신 조각으로 보관한다.
          const period = dateRange.length === 1 ? dateRange[0] : `${dateRange[0]} ~ ${dateRange[dateRange.length - 1]}`;
          setMetricBucket({ period, horizon: historyHorizon });
          const customAlgo = response.data?.customAlgorithm;
          if (customAlgo) {
            algorithmListRef.current = customAlgo;
          }
        }
        else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: `[${t("forecastMetricsResult")}] ${t("MSG_NoData")}` });
          setMetricDatas([]);
        }
      }
      setMetricGridHeader();
    })
    .catch(e => {
      debug(e);
      setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      setMetricDatas([]);
    })
    .then(() => {
      const reduxObjState = reduxUtil.createObjectState(viewName, {...params});
      reduxDispatch(reduxUtil.updateViewState({ ...reduxObjState }));
      setTreeload(false);
      retrieveAlgorithmData(custHrchies, itemHrchies);
    });
  }

  const retrieveCustHrchies = () => {
    customerHrchyService.getTopHrchy({ hrchyNum: codeUtil.hrchyNum.HRCHY, isPermnCheck: true })
      .then(response => {
        const hrchy = response.data;
        if (hrchy !== null) {
          setDefaultCustomerHrchy(hrchy);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveItemHrchies = () => {
    itemHrchyService.getTopHrchy({ hrchyNum: codeUtil.hrchyNum.HRCHY, isPermnCheck: true })
    .then(response => {
      const hrchy = response.data;
      if (hrchy !== null) {
        setDefaultValue(hrchy);
      }
    })
    .catch(e => {
      debug(e);
    });
  };

  const getRowStyle = (params) => {
    if (params.node.lastChild) {
      return { borderBottom: "1.5px solid #d9dcde" };
    }
    return null;
  };

  const getRowHeight = () => {
    return 35;
  }

  const handleReset = () => {
    reduxDispatch(reduxUtil.updateViewState({}));
    itemSearch.current = "";
    customerSearch.current = "";
    setTreeload(true);
    setReload(true);
  };

  const onGridReady = (gridId) => {
    if (gridId === METRICS_GRID) {
      setMetricsGridLoading(true);
    } else if (gridId === ALGORITHM_GRID) {
      setAlgorithmGridLoading(true);
    }
  }

  useEffect(() => {
    if(!isEmpty(metricsGridRef.current) && metricsGridLoading) {
      GridUtils.setColumnDefs({ ...metricsGridRef.current, viewName, gridId: METRICS_GRID, columnDefs: metricColumnDefs, initState: true })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricColumnDefs, metricsGridLoading]);

  useEffect(() => {
    if(!isEmpty(algorithmGridRef.current) && algorithmGridLoading) {
      GridUtils.setColumnDefs({ ...algorithmGridRef.current, viewName, gridId: ALGORITHM_GRID, columnDefs, initState: true })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, algorithmGridLoading]);

  useEffect(() => {
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) {
      if (size(reduxViewState?.algorithm) < 1) {
        retrieveItemHrchies();
        retrieveCustHrchies();
      }
      retrieveParameters();
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (filterValue.customerHrchy && filterValue.category && reload && algorithmListRef?.current?.length > 0 && Object.keys(bfParameters).length > 0) {
      retrieveMetrics(filterValue.customerHrchy?.hrchyCd, filterValue.category?.hrchyCd);
    }
    setReload(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    if (filterValue.customerHrchy && filterValue.category && algorithmListRef?.current?.length > 0 && Object.keys(bfParameters).length > 0) {
      retrieveMetrics(filterValue.customerHrchy?.hrchyCd, filterValue.category?.hrchyCd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue])

  return (
    <>
      {/* hasIcons={false} — 우측에 밀어 붙일 액션이 없다. 안 주면 FilterContainer 가
          마지막 자식(Reset)을 ml:auto 로 우측에 붙여 DP Workbench 와 위치가 달라진다 */}
      <FilterContainer hasIcons={false}>
        <Box sx={{ position: "relative", display: "flex" }}>
          <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent defaultValue={defaultCustomerHrchy} treeload={treeload} styles={{ width: 290 }}/>
          <CustomerAutocomplete
            retrieve={() => retrieveMetrics(filterValue.customerHrchy?.hrchyCd, filterValue.category?.hrchyCd)}
            searchFields={ElasticSearchUtil.defaultCustomerSearchFields}
            searchValue={customerSearch}
            sx={{ mr: 1.5 }}
          />
        </Box>
        <Box sx={{ position: "relative", display: "flex" }}>
          <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent defaultValue={defaultValue} treeload={treeload} styles={{ width: 290 }} />
          <DPItemAutocomplete
            retrieve={() => retrieveMetrics(filterValue.customerHrchy?.hrchyCd, filterValue.category?.hrchyCd)}
            searchFields={ElasticSearchUtil.defaultItemSearchFields}
            searchValue={itemSearch}
          />
          {/* 검색창 바로 뒤 — DP Workbench 와 같은 자리. 입력창 하단에 맞춘다 */}
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <ResetButton onClick={handleReset} />
          </Box>
        </Box>
      </FilterContainer>
      {/* grid-area(.overflow:auto) 안을 flex column 으로: 카드 행은 고정, 콘텐츠는 남은 공간.
          #bf-review-card 는 SCSS 가 height:120px 를 강제하므로 픽셀 calc 대신 flex 로 나눠 오버플로우를 없앤다. */}
      <Box className={"grid-area"} sx={{ display: "flex", flexDirection: "column" }}>
        <Box id="bf-review-card" sx={{ display: "flex", width: "100%", mb: 1.5, flexShrink: 0 }}>
          {/* 정보 카드: 제목 헤더(위) + 값 강조(아래). 알고리즘은 칩(색은 BfAlgorithmSettings 와 같은 ColorUtil.tintChipSx). */}
          <AppCard key="stamp" sx={{ width: "33%", mr: 1 }}>
            <CardContent>
              <Typography sx={CARD_HEADER_SX}>{t("forecastRunTime")}</Typography>
              {isArray(createTs) && createTs.map((row, i) => (
                <Box key={`ts${i}`} sx={{ display: "flex", gap: 1, mt: 0.25 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>{row.label}</Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>{row.value}</Typography>
                </Box>
              ))}
            </CardContent>
          </AppCard>
          <AppCard key="metric" sx={{ width: "33%", mr: 1 }}>
            <CardContent>
              <Typography sx={CARD_HEADER_SX}>{t("forecastMetrics")}</Typography>
              <Typography sx={{ fontSize: FS.KPI, fontWeight: 500, mt: 0.5 }}>{metrics}</Typography>
            </CardContent>
          </AppCard>
          <AppCard key="method" sx={{ width: "33%", mr: 1 }}>
            <CardContent>
              <Typography sx={CARD_HEADER_SX}>{t("forecastAlgorithmList")}</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                {isArray(targetList) && targetList.map(name => (
                  <Chip key={name} label={name} size="small"
                    sx={ColorUtil.tintChipSx(ColorUtil.algorithmColor?.[name])} />
                ))}
              </Box>
            </CardContent>
          </AppCard>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* 지표는 3행 고정 — 최대 높이를 넉넉히 줘서 히트맵이 세로 스크롤 없이 들어가게 한다.
              (알고리즘 열이 늘면 히트맵 Box 가 가로로만 스크롤) */}
          <Box sx={{ height: "25%", maxHeight: 220, mb: 2, flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, maxHeight: 140 }}>
              <Typography variant="h6" sx={SECTION_TITLE_SX}>
                {t("forecastMetricsResult")}
              </Typography>
              {/* 기간 · History buckets 를 각각 작은 칩으로. 값 조각은 metricBucket = { period, horizon } */}
              {metricBucket && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip size="small" variant="outlined"
                    icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />}
                    label={metricBucket.period} />
                  <Chip size="small" variant="outlined"
                    label={<span>{t("historyHorizon")} <b>{metricBucket.horizon}</b></span>} />
                </Box>
              )}
            </Box>
            {/* 지표 × 알고리즘 오차 히트맵. 데이터(metricDatas)·컬럼(metricColumnDefs)은 기존 파이프라인 그대로 쓴다. */}
            <Box id="bf-review-grid" sx={{ height: "calc(100% - 40px)", mb: 3, overflow: "auto" }} >
              <MetricsHeatmap
                rows={metricDatas}
                columns={metricColumnDefs.filter(c => c.field !== "metric").map(c => c.field)}
              />
            </Box>
          </Box>
          <Box>
            <Box className="bf-review-typo">
              <Typography key="stamp1" variant="h6" sx={{ ...SECTION_TITLE_SX, mb: 1 }}>
                {t("forecastAlgorithmResult")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <SplitterContainer initialSizes={[1, 1]}  collapseDirection='up'>
              <Box className="chart-area" sx={{ width: "100%", height: "100%" }}>
                <ChartComposed
                  xAxis={["yyyymmdd"]}
                  data={algorithmForecastChart}
                  dataSeries={chartSeries}
                  tooltipAnimation={false}
                  options={{ minHeight: "150px" }}
                />
              </Box>
              <Box className="ag-theme-balham" sx={{ height: "calc(100% - 20px)", mt: 2 }} >
                <AgGridReact
                  ref={algorithmGridRef}
                  {...defaultAlgorithmGridMemo}
                  rowData={algorithmForecast}
                  onGridReady={(_) => onGridReady(ALGORITHM_GRID)}
                  columnDefs={GridUtils.getColumnDefs(columnDefs)}
                  overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
                  getRowStyle={getRowStyle}
                  getRowHeight={getRowHeight}
                  suppressAggFuncInHeader
                />
              </Box>
            </SplitterContainer>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
      />
      {/* AI 채팅 위젯 (useAutoAnalysis 훅) */}
      {/*{ChatWidgetComponent}*/}
    </>
  )
}

export default withTranslation()(BfReview);
